import { hasDocument, hasLocalStorage } from '../utils/safe-env';

/**
 * Contrato común de todos los backends de almacenamiento persistente.
 * Las claves se guardan internamente con el prefijo `veo:`.
 */
export interface StorageAdapter {
  get(key: string): string | null;
  set(key: string, value: string, options?: { ttlMs?: number }): void;
  remove(key: string): void;
  /** Borra únicamente las claves del SDK (prefijo `veo:`). */
  clear(): void;
}

const STORAGE_PREFIX = 'veo:';
/** TTL por defecto para cookies (1 año), satisface la persistencia del anonymous id. */
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/** Backend basado en `localStorage`. Persistencia indefinida (ignora `ttlMs`). */
class LocalStorageAdapter implements StorageAdapter {
  get(key: string): string | null {
    return localStorage.getItem(STORAGE_PREFIX + key);
  }

  set(key: string, value: string): void {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch {
      // Cuota llena o storage bloqueado: ignorar silenciosamente.
    }
  }

  remove(key: string): void {
    localStorage.removeItem(STORAGE_PREFIX + key);
  }

  clear(): void {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  }
}

/** Lee y trocea `document.cookie`. Solo se invoca cuando hay `document`. */
function readCookies(): string[] {
  const raw = document.cookie;
  return raw === '' ? [] : raw.split('; ');
}

/** Backend basado en cookies de primer nivel. Usa `ttlMs` (default 1 año). */
class CookieAdapter implements StorageAdapter {
  get(key: string): string | null {
    const name = `${STORAGE_PREFIX}${key}=`;
    for (const cookie of readCookies()) {
      if (cookie.startsWith(name)) {
        return decodeURIComponent(cookie.slice(name.length));
      }
    }
    return null;
  }

  set(key: string, value: string, options?: { ttlMs?: number }): void {
    const maxAgeSec = Math.floor((options?.ttlMs ?? ONE_YEAR_MS) / 1000);
    const secure =
      typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    // biome-ignore lint/suspicious/noDocumentCookie: necesitamos escritura síncrona y cero deps; la Cookie Store API es async y no universal.
    document.cookie = `${STORAGE_PREFIX}${key}=${encodeURIComponent(
      value,
    )}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
  }

  remove(key: string): void {
    // biome-ignore lint/suspicious/noDocumentCookie: ver set(); expiramos la cookie con Max-Age=0.
    document.cookie = `${STORAGE_PREFIX}${key}=; Path=/; Max-Age=0; SameSite=Lax`;
  }

  clear(): void {
    for (const cookie of readCookies()) {
      const eq = cookie.indexOf('=');
      const name = eq === -1 ? cookie : cookie.slice(0, eq);
      if (name.startsWith(STORAGE_PREFIX)) {
        this.remove(name.slice(STORAGE_PREFIX.length));
      }
    }
  }
}

/** Backend en memoria. Fallback cuando no hay browser storage (SSR, incógnito). */
class MemoryAdapter implements StorageAdapter {
  private store = new Map<string, string>();

  get(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  set(key: string, value: string): void {
    this.store.set(key, value);
  }

  remove(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }
}

/**
 * Crea el adapter de almacenamiento, respetando la preferencia del usuario y
 * cayendo a la mejor alternativa disponible si la preferida no existe.
 */
export function createStorage(
  preferred: 'localStorage' | 'cookie' | 'memory' = 'localStorage',
): StorageAdapter {
  if (preferred === 'localStorage' && hasLocalStorage()) return new LocalStorageAdapter();
  if (preferred === 'cookie' && hasDocument()) return new CookieAdapter();
  if (preferred === 'memory') return new MemoryAdapter();

  // Cadena de fallback.
  if (hasLocalStorage()) return new LocalStorageAdapter();
  if (hasDocument()) return new CookieAdapter();
  return new MemoryAdapter();
}
